import secureLog from './secureLogger';
import { getConfig } from './secureConfig';

/**
 * Initialize Google Analytics with proper error handling
 * @returns {boolean} Whether initialization was successful
 */
export const initGoogleAnalytics = () => {
  try {
    // Get Google Analytics ID from multiple possible sources
    const googleAnalyticsId = 
      getConfig('analytics.googleAnalyticsId') || 
      process.env.REACT_APP_GOOGLE_ANALYTICS_ID ||
      process.env.REACT_APP_GA_ID;
    
    if (!googleAnalyticsId) {
      secureLog.warn('Google Analytics ID not configured');
      return false;
    }

    // Check if already initialized
    if (window.gtag && window.dataLayer) {
      secureLog.info('Google Analytics already initialized');
      return true;
    }
    
    // Create script element for gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
    
    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    
    // Initialize GA4
    gtag('js', new Date());
    gtag('config', googleAnalyticsId, { 
      send_page_view: false,
      debug_mode: process.env.NODE_ENV === 'development',
      transport_url: 'https://www.google-analytics.com'
    });
    
    // Append script to document
    document.head.appendChild(script);
    
    // Add error handling for script load
    script.onerror = (error) => {
      secureLog.error('Failed to load Google Analytics script:', error);
      return false;
    };
    
    script.onload = () => {
      secureLog.info('Google Analytics initialized with ID:', googleAnalyticsId);
      return true;
    };
    
    return true;
  } catch (error) {
    secureLog.error('Error initializing Google Analytics:', error);
    return false;
  }
};

/**
 * Track a conversion event in Google Analytics with robust error handling
 * @param {string} eventName - Name of the event to track
 * @param {Object} params - Event parameters
 * @returns {Promise<boolean>} Whether tracking was successful
 */
export const trackConversion = (eventName, params = {}) => {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined' || !window.gtag) {
        secureLog.warn(`Google Analytics not available for event: ${eventName}`);
        
        // Try dataLayer as fallback
        if (window.dataLayer) {
          window.dataLayer.push({
            event: eventName,
            ...params
          });
          secureLog.debug(`Used dataLayer fallback for event: ${eventName}`);
          resolve(true);
          return;
        }
        
        resolve(false);
        return;
      }
      
      // Set a timeout to resolve the promise after 1.5 seconds
      const timeout = setTimeout(() => {
        secureLog.warn(`Google Analytics event timed out: ${eventName}`);
        resolve(false);
      }, 1500);
      
      // Send the event with a callback
      window.gtag('event', eventName, {
        ...params,
        event_callback: () => {
          clearTimeout(timeout);
          secureLog.debug(`Google Analytics event confirmed sent: ${eventName}`);
          resolve(true);
        }
      });
      
      secureLog.debug(`Google Analytics event fired: ${eventName}`);
    } catch (error) {
      secureLog.error(`Error tracking Google Analytics event: ${eventName}`, error);
      resolve(false);
    }
  });
};

/**
 * Track a page view in Google Analytics
 * @param {string} path - Page path to track
 * @returns {Promise<boolean>} Whether tracking was successful
 */
export const trackPageView = (path) => {
  return trackConversion('page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title
  });
}; 